import React, { useEffect, useState } from 'react';
import { View, ScrollView } from 'react-native';
import { translate } from '../../../services/translate';
import { NavigationProp, RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { DocumentsParamList } from '../../../typings/Params';
import { documentService } from '../../../services/central-api/document';
import { ClientError } from '../../../services/central-api/base-service';
import Toast from 'react-native-toast-message';
import { useForm } from 'react-hook-form';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import { Tabs } from '../../../components/Tabs';
import CustomButton from '../../../components/CustomButton';
import MyAppText from '../../../components/MyAppText';
import MyDatePicker from '../../../components/MyDatePicker';
import FilterDropdown from '../../../components/Filter/components/FilterDropdown';
import { RichTextEditor } from '../../../components/RichTextEditor';
import WarningModal from '../../../components/WarningModal';
import moment from 'moment';
import { TermsDocumentType, TermsLanguage, Application, termsDocumentTypes, termsLanguages } from '../../../schemas/documents';
import Divider from '../../../components/Divider';

const APPLICATION: Application = 'platform';

interface TermsForm {
    documentType: TermsDocumentType | '';
    language: TermsLanguage | '';
    effectiveDate: Date | null;
    htmlContent: string;
}

function isEmptyHtml(html: string) {
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length === 0;
}

function normalizeEffectiveDate(value: Date) {
    return moment(value).startOf('day');
}

export default function TermsAndPrivacyPlatformForm() {
    const navigation = useNavigation<NavigationProp<DocumentsParamList>>();
    const route = useRoute<RouteProp<DocumentsParamList, 'TermsAndPrivacyPlatformForm'>>();
    const termsId = route.params?.id;
    const versionNumber = route.params?.version;
    const mode = route.params?.mode;
    const { styles, theme } = useStyles(stylesheet);
    const isView = mode === 'view';
    const isDuplicate = mode === 'duplicate';
    const isEditScheduled = mode === 'editScheduled';
    const isEdit = !!termsId && !mode;
    const [isLoaded, setIsLoaded] = useState(!termsId);

    const [scheduledExistsVisible, setScheduledExistsVisible] = useState(false);

    const [loadedVersion, setLoadedVersion] = useState<{
        documentType: TermsDocumentType;
        language: TermsLanguage;
        version: number;
        effectiveDate: string;
    }>();

    const [datePickerZIndex, setDatePickerZIndex] = useState(3);

    const defaultEmptyValues: TermsForm = {
        documentType: '',
        language: '',
        effectiveDate: null,
        htmlContent: '',
    };

    const form = useForm<TermsForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange',
    });

    async function getTerms(id: number) {
        try {
            const terms = await documentService.getTermsOfUseAndPrivacyById(id);
            let htmlContent = terms.versions?.[0]?.htmlContent ?? '';
            let effectiveDate: Date | null = null;

            if (versionNumber) {
                const version = await documentService.getTermsOfUseAndPrivacyVersion(id, versionNumber);
                htmlContent = version.htmlContent ?? '';

                setLoadedVersion({
                    documentType: terms.documentType,
                    language: terms.language,
                    version: version.version,
                    effectiveDate: version.effectiveDate,
                });

                effectiveDate = isDuplicate
                    ? null
                    : moment(version.effectiveDate).startOf('day').toDate();
            }

            form.reset({
                documentType: terms.documentType,
                language: terms.language,
                effectiveDate,
                htmlContent,
            });
            setIsLoaded(true);
        } catch (err) {
            console.error(err);
            Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
            navigation.goBack();
        }
    }

    useEffect(() => {
        setIsLoaded(false);
        setLoadedVersion(undefined);

        if (termsId) {
            getTerms(termsId);
        } else {
            form.reset(defaultEmptyValues);
            setIsLoaded(true);
        }
    }, [termsId, versionNumber, mode]);

    function isFormValid() {
        const data = form.getValues();
        if (isEmptyHtml(data.htmlContent) || !data.effectiveDate) {
            return false;
        }
        if (!isEdit && (!data.documentType || !data.language)) {
            return false;
        }
        return true;
    }

    async function handleSave() {
        try {
            const data = form.getValues();

            if (!data.effectiveDate) {
                return;
            }

            const effectiveDate = normalizeEffectiveDate(data.effectiveDate);

            if (effectiveDate.isBefore(moment().startOf('day'))) {
                return Toast.show({ type: 'sentinelxError', text1: translate('effectiveDateInPast') });
            }

            if (isEditScheduled) {
                await formSubmit();
                return;
            }

            const scheduled = await documentService.getAllTermsOfUseAndPrivacyVersions({
                status: 'scheduled',
                documentType: data.documentType as TermsDocumentType,
                application: APPLICATION,
                language: data.language as TermsLanguage,
                page: 0,
                limit: 100,
            });

            if (scheduled.rows.length) {
                setScheduledExistsVisible(true);
                return;
            }

            await formSubmit();
        } catch (err) {
            console.error(err);
            Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
        }
    }

    async function formSubmit() {
        try {
            const data = form.getValues();

            if (isEmptyHtml(data.htmlContent) || !data.effectiveDate) {
                return;
            }

            const effectiveDate = normalizeEffectiveDate(data.effectiveDate).toDate();

            if (isEditScheduled && termsId && versionNumber) {
                await documentService.updateTermsOfUseAndPrivacyVersion(termsId, versionNumber, {
                    effectiveDate,
                    htmlContent: data.htmlContent,
                });
            } else if (isEdit) {
                await documentService.updateTermsOfUseAndPrivacy(termsId, {
                    effectiveDate,
                    htmlContent: data.htmlContent,
                });
            } else {
                if (!data.documentType || !data.language) {
                    return;
                }
                await documentService.createTermsOfUseAndPrivacy({
                    documentType: data.documentType,
                    language: data.language,
                    application: APPLICATION,
                    effectiveDate,
                    htmlContent: data.htmlContent,
                    isDuplicate
                });
            }

            Toast.show({ type: 'sentinelxSuccess', text1: translate('ActionSuccessfully') });
            navigation.navigate('TermsAndPrivacyPlatformApp', { page: 1, limit: 25 });
        } catch (err) {
            if (err instanceof ClientError && err.message === 'TermsScheduledVersionAlreadyExists') {
                setScheduledExistsVisible(true);
                return;
            }
            if (err instanceof ClientError) {
                return Toast.show({ type: 'sentinelxError', text1: translate(err.message) });
            }
            console.error(err);
            Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
        }
    }

    function getTitle() {
        if ((isView || isEditScheduled) && loadedVersion) {
            const { documentType, language, version, effectiveDate } = loadedVersion;
            return `${translate(documentType)} (${version}) - ${language.toUpperCase()} - ${moment(effectiveDate).format('DD/MM/YYYY')}`;
        }

        return translate(
            isView ? 'viewTermsAndPrivacy'
                : isEditScheduled ? 'editScheduledTermsAndPrivacy'
                    : isEdit ? 'updateTermsAndPrivacy'
                        : 'registerTermsAndPrivacy'
        );
    }

    const documentTypeItems = termsDocumentTypes.map(t => ({ value: t, label: translate(t) }));
    const languageItems = termsLanguages.map(l => ({ value: l, label: translate(l) }));

    const htmlContent = form.watch('htmlContent');

    return (
        <Container>
            <Header
                title={getTitle()}
                actions={isView ? undefined : <CustomButton
                    text={translate('save')}
                    disabled={!isFormValid()}
                    onPress={handleSave}
                />}
                goBackAction={() => {
                    navigation.canGoBack()
                        ? navigation.goBack()
                        : navigation.navigate('TermsAndPrivacyPlatformApp', { page: 1, limit: 25 });
                }}
            />

            <Card>
                <Tabs tabs={[{
                    key: 'register',
                    label: translate('documentInfo'),
                }]}>
                    {({ selectedTab }) => {
                        if (selectedTab === 'register') {
                            return (
                                <ScrollView contentContainerStyle={{ flexDirection: 'column', gap: 8, padding: 10 }}>
                                    <MyAppText style={styles.sectionTitle()}>
                                        {translate('generalData')}
                                    </MyAppText>
                                    <Divider
                                        color={theme.colors.primary['200']}
                                        thickness={1}
                                        style={{ width: '100%' }}
                                    />
                                    <View style={styles.fieldsRow}>
                                        <View style={[styles.field, { zIndex: 5 }]}>
                                            <FilterDropdown
                                                label={translate('documentType')}
                                                required
                                                semiBoldLabel={true}
                                                labelFontSize={14}
                                                disabled={isEdit || isView || isEditScheduled}
                                                items={documentTypeItems}
                                                multiple={false}
                                                value={form.watch('documentType')}
                                                setValue={(val) => {
                                                    const resolved = typeof val === 'function' ? val(form.watch('documentType')) : val;
                                                    form.setValue('documentType', resolved as TermsDocumentType, { shouldValidate: true });
                                                }}
                                                zIndex={5}
                                            />
                                        </View>

                                        <View style={[styles.field, { zIndex: 4 }]}>
                                            <FilterDropdown
                                                label={translate('language')}
                                                required
                                                semiBoldLabel={true}
                                                labelFontSize={14}
                                                disabled={isEdit || isView || isEditScheduled}
                                                items={languageItems}
                                                multiple={false}
                                                value={form.watch('language')}
                                                setValue={(val) => {
                                                    const resolved = typeof val === 'function' ? val(form.watch('language')) : val;
                                                    form.setValue('language', resolved as TermsLanguage, { shouldValidate: true });
                                                }}
                                                zIndex={4}
                                            />
                                        </View>

                                        <View style={[styles.field, { zIndex: datePickerZIndex }]}>
                                            <MyDatePicker
                                                label={translate('effectiveDate')}
                                                form={form}
                                                dateFieldName='effectiveDate'
                                                required={!isView}
                                                disabled={isView}
                                                minDate={isView ? undefined : moment().startOf('day').toDate()}
                                                labelStyle={styles.inputTitle}
                                                zIndex={datePickerZIndex}
                                                onCalendarOpen={() => setDatePickerZIndex(99999)}
                                                onCalendarClose={() => setDatePickerZIndex(3)}
                                            />
                                        </View>
                                    </View>

                                    <View style={{ gap: 8, zIndex: 2 }}>
                                        <View style={{ flexDirection: 'row', gap: 4 }}>
                                            <MyAppText style={styles.sectionTitle()}>
                                                {translate('documentContent')}
                                            </MyAppText>
                                            <MyAppText style={{ color: theme.colors.errors['300'], alignSelf: 'flex-end' }}>(*)</MyAppText>
                                        </View>
                                        <Divider
                                            color={theme.colors.primary['200']}
                                            thickness={1}
                                            style={{ width: '100%' }}
                                        />
                                        <View style={styles.editorContainer}>
                                            {isLoaded
                                                ? <RichTextEditor
                                                    text={htmlContent}
                                                    editable={!isView}
                                                    onChangeText={(value) => {
                                                        form.setValue('htmlContent', value, { shouldValidate: true });
                                                    }}
                                                />
                                                : null}
                                        </View>
                                    </View>
                                </ScrollView>
                            );
                        }
                    }}
                </Tabs>
            </Card>

            <WarningModal
                singleButton
                cancelText='close'
                isModalVisible={scheduledExistsVisible}
                setIsModalVisible={setScheduledExistsVisible}
                text={<>
                    <MyAppText style={{ fontWeight: 'bold' }}>{translate('scheduledTermsExistsTitle')}</MyAppText>
                    {'\n\n'}
                    {translate('TermsScheduledVersionAlreadyExists')}
                </>}
                onSubmit={() => setScheduledExistsVisible(false)}
                onClose={() => setScheduledExistsVisible(false)}
            />
        </Container>
    );
}

const stylesheet = createStyleSheet((theme) => ({
    inputTitle: {
        fontSize: 14,
        color: theme.colors.neutral[500],
        fontWeight: '600',
    },
    filterTextRequired: {
        color: theme.colors.errors['300'],
        fontWeight: '600',
    },
    fieldsRow: {
        flexDirection: {
            xs: 'column',
            md: 'row',
        },
        gap: 16,
        zIndex: 5,
    },
    field: {
        flex: 1,
    },
    editorContainer: {
        height: 450,
    },
    sectionTitle: (padding: number = 16) => ({
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.primary['500'],
        paddingTop: padding
    }),
}));
